<template>
  <f7-page name="group-marketplace-create">
    <f7-navbar :title="navbarTitle" back-link>
      <f7-nav-right v-if="step === 1">
        <f7-link @click="goBack"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block v-if="!canPublish" class="mktc-notallowed">
          <f7-icon f7="lock_fill" size="42" color="#8e8e93" />
          <p>{{ $t("marketplace.notAllowed") }}</p>
        </f7-block>

        <template v-else-if="step === 1">
          <f7-block-title>{{ $t("marketplace.whatCreate") }}</f7-block-title>
          <f7-list inset strong>
            <f7-list-item
              v-for="k in kinds"
              :key="k.code"
              :title="$t(kindLabelKey(k.code))"
              :subtitle="$t(kindDescKey(k.code))"
              link
              @click="selectKind(k.code)"
            >
              <template #media>
                <span class="mktc-kind-icon" :style="{ background: k.color }">
                  <f7-icon :f7="k.icon" size="18" color="white" />
                </span>
              </template>
            </f7-list-item>
          </f7-list>
        </template>

        <template v-else>
          <f7-block>
            <div
              class="mktc-kind-selected"
              :style="{ background: selectedKind.color }"
            >
              <f7-icon :f7="selectedKind.icon" size="16" color="white" />
              <span>{{ $t(kindLabelKey(form.kind)) }}</span>
            </div>
          </f7-block>

          <f7-list inset strong no-hairlines>
            <f7-list-input
              type="text"
              :label="$t('marketplace.formTitle')"
              :placeholder="$t('marketplace.formTitlePh')"
              :value="form.title"
              @input="form.title = $event.target.value"
            />
            <f7-list-input
              type="textarea"
              resizable
              :label="$t('marketplace.formDesc')"
              :placeholder="$t('marketplace.formDescPh')"
              :value="form.description"
              @input="form.description = $event.target.value"
            />
            <f7-list-input
              type="text"
              inputmode="numeric"
              :label="$t('marketplace.formPrice')"
              :placeholder="$t('marketplace.formPricePh')"
              :value="form.price"
              @input="form.price = $event.target.value.replace(/[^0-9]/g, '')"
            ><template #media><span class="sel-listing-currency">{{form.currency}}</span></template></f7-list-input>
            <p class="mktc-hint">{{ $t("marketplace.priceHint") }}</p>
            <f7-list-item :title="$t('marketplace.negotiableLabel')">
              <template #after>
                <f7-toggle v-model:checked="form.negotiable" color="green" />
              </template>
            </f7-list-item>
            <f7-list-input
              type="select"
              :label="$t('marketplace.formCategory')"
              :value="form.category"
              @change="form.category = $event.target.value"
            >
              <option value=""></option>
              <option v-for="c in categories" :key="c" :value="c">
                {{ categoryLabel(c) }}
              </option>
            </f7-list-input>
          </f7-list>

          <div class="mktc-question">{{ $t("marketplace.contactMethod") }}</div>
          <f7-list inset strong>
            <f7-list-item
              v-for="c in CONTACT_METHODS"
              :key="c.code"
              radio
              radio-icon="start"
              :title="contactLabel(c)"
              name="contactMethod"
              :checked="form.contactMethod === c.code"
              @change="form.contactMethod = c.code"
            />
            <f7-list-input
              v-if="form.contactMethod !== 'CHAT'"
              type="tel"
              inputmode="tel"
               ref="phoneInputRef"
              :label="$t('marketplace.contactValue')"
              :value="form.contactValue"
              @input="form.contactValue = $event.target.value"
            />
          </f7-list>

          <f7-block class="mktc-image">
            <f7-block-title>{{ $t("marketplace.formImage") }}</f7-block-title>
            <ImageDropzone
              :label="$t('marketplace.formImage')"
              :hint="$t('marketplace.formImageHint')"
              shape="square"
              v-model="form.media"
            />
          </f7-block>

          <f7-block >
             <f7-button large outline raised class="mktc-back" @click="step = 1">
              {{ $t("marketplace.back") }}
            </f7-button>
            <f7-button large fill raised @click="submit">
              {{
                requiresApprovalForMe
                  ? $t("marketplace.submitPending")
                  : $t("marketplace.submit")
              }}
            </f7-button>
           
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import ImageDropzone from "../components/common/ImageDropzone.vue";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import { useMembersStore } from "../js/pinia-stores/members.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useAccountStore } from "../js/pinia-stores/account.js";
import { useTelephone } from "../js/composables/useTelephone.js";


import {
  useMarketplaceStore,
  OFFER_KINDS,
  OFFER_CATEGORIES,
  getCategoryLabel,
} from "../js/pinia-stores/marketplace.js";
import { CONTACT_METHODS } from "../constants/listing-options.js";
import i18n from "../js/i18n.js";

const props = defineProps({ groupId: { type: String, required: true } });

const { t } = i18n.global;
const groupsStore = useGroupsStore();
const membersStore = useMembersStore();
const keycloakStore = useKeycloakStore();
const marketplaceStore = useMarketplaceStore();
const accountStore=useAccountStore();

const step = ref(1);
const form = reactive({
  kind: "",
  title: "",
  description: "",
  price: "",
  currency:"",
  negotiable: false,
  category: "",
  contactMethod: "WHATSAPP",
  contactValue: "",
  media: "",
});
const phoneInputRef = ref(null);
useTelephone(phoneInputRef);
const group = computed(() => groupsStore.activeGroup);
const currentUserId = computed(() => keycloakStore.subject || "");
const marketplaceEnabled = computed(
  () =>
    group.value?.features?.includes("MARKETPLACE") ||
    group.value?.marketplace?.enabled === true,
);
const marketplaceSettings = computed(() => group.value?.marketplace || null);
const requiresApproval = computed(
  () => marketplaceSettings.value?.requiresApproval !== false,
);
const whoCanPublish = computed(
  () => marketplaceSettings.value?.whoCanPublish || "MEMBERS_APPROVAL",
);
const listingExpiryDays = computed(
  () => marketplaceSettings.value?.listingExpiryDays || 30,
);
const sellerPostLimit = computed(
  () => marketplaceSettings.value?.sellerPostLimit || 3,
);

const isAdmin = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === currentUserId.value,
  );
  return viewer?.role === "OWNER" || viewer?.role === "ADMIN";
});

const isMember = computed(() =>
  membersStore.members.some((m) => m.userId === currentUserId.value),
);

const canPublish = computed(() => {
  if (!marketplaceEnabled.value) return false;
  if (!isMember.value) {
    return marketplaceSettings.value?.allowExternalSellers === true;
  }
  if (whoCanPublish.value === "ADMINS_ONLY") return isAdmin.value;
  if (whoCanPublish.value === "APPROVED_SELLERS") {
    if (isAdmin.value) return true;
    return marketplaceStore
      .published(props.groupId)
      .some((o) => o.sellerUserId === currentUserId.value);
  }
  return true;
});

const requiresApprovalForMe = computed(
  () => requiresApproval.value || !isMember.value,
);

const atListingLimit = computed(
  () =>
    marketplaceStore.activeListingsCount(props.groupId, currentUserId.value) >=
    sellerPostLimit.value,
);

const kinds = OFFER_KINDS;
const categories = Object.keys(OFFER_CATEGORIES);
const categoryLabel = (code) => getCategoryLabel(code);
const selectedKind = computed(
  () => OFFER_KINDS.find((k) => k.code === form.kind) || OFFER_KINDS[0],
);

const navbarTitle = computed(() =>
  step.value === 1 ? t("marketplace.whatCreate") : t("marketplace.createOffer"),
);

function kindLabelKey(code) {
  return {
    PERSONAL_OFFER: "marketplace.kindPersonal",
    BUSINESS_SHOP: "marketplace.kindShop",
    PRODUCT_LISTING: "marketplace.kindProduct",
    SERVICE_LISTING: "marketplace.kindService",
  }[code];
}
function kindDescKey(code) {
  return {
    PERSONAL_OFFER: "marketplace.kindPersonalDesc",
    BUSINESS_SHOP: "marketplace.kindShopDesc",
    PRODUCT_LISTING: "marketplace.kindProductDesc",
    SERVICE_LISTING: "marketplace.kindServiceDesc",
  }[code];
}
function contactLabel(c) {
  return (
    {
      WHATSAPP: t("marketplace.contactWhatsapp"),
      CHAT: t("marketplace.contactChat"),
      PHONE: t("marketplace.contactPhone"),
    }[c.code] || c.code
  );
}

function selectKind(code) {
  form.kind = code;
  step.value = 2;
}

function goBack() {
  f7.views.main.router.back();
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId),
    membersStore.fetchMembers(props.groupId).catch(() => {}),
  ]);
});

async function submit() {
  if (!form.kind) {
    step.value = 1;
    return;
  }
  if (!form.title.trim() || !form.description.trim()) {
    f7.dialog.alert(t("marketplace.formRequired"));
    return;
  }
  if (atListingLimit.value && !isAdmin.value) {
    f7.toast.show({
      text: t("marketplace.limitReached", { limit: sellerPostLimit.value }),
      closeTimeout: 3000,
    });
    return;
  }

  const member = membersStore.members.find(
    (m) => m.userId === currentUserId.value,
  );
  const sellerName =
    member?.name ||
    keycloakStore.userInfo?.name ||
    keycloakStore.userInfo?.given_name || accountStore.userAccount?.name ||
    "";

  const offer = {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 9),
    groupId: props.groupId,
    kind: form.kind,
    title: form.title.trim(),
    description: form.description.trim(),
    price: form.price === "" ? null : Number(form.price),
    currency:form.currency,
    negotiable: form.negotiable,
    category: form.category || "Autre",
    contactMethod: form.contactMethod,
    contactValue: form.contactMethod === "CHAT" ? "" : form.contactValue.trim(),
    media: form.media ? [form.media] : [],
    sellerUserId: currentUserId.value,
    sellerName,
    sellerVerified: !!member?.verified,
    status: requiresApprovalForMe.value ? "PENDING" : "PUBLISHED",
    featured: false,
    submissions: [],
    createdAt: new Date().toISOString(),
    expiresAt: new Date(
      Date.now() + listingExpiryDays.value * 86400000,
    ).toISOString(),
  };
  if (!requiresApprovalForMe.value) {
    offer.publishedAt = new Date().toISOString();
  }

  try {
    await marketplaceStore.submitOffer(offer);
    f7.toast.show({
      text: requiresApprovalForMe.value
        ? t("marketplace.submitted")
        : t("marketplace.publishedNow"),
      closeTimeout: 2500,
    });
    f7.views.main.router.back();
  } catch (e) {
    f7.dialog.confirm(
      t("marketplace.sellerProfileNeeded"),
      t("marketplace.sellerProfileTitle"),
      () => {
        f7.views.main.router.navigate("/sell");
      },
    );
  }
}
</script>

<style scoped>
.mktc-notallowed {
  text-align: center;
  padding: 48px 24px;
}
.mktc-notallowed p {
  font-size: 14px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 10px;
}
.mktc-kind-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mktc-kind-selected {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 6px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}
.mktc-kind-selected .f7-icon {
  background: transparent;
}
.mktc-question {
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-theme-color);
  padding: 12px 16px 0;
}
.mktc-hint {
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: -4px 16px 8px;
}
.mktc-image :deep(.image-dropzone) {
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
}
.mktc-back {
  margin-top: 8px;
}
</style>
